import React from 'react';
import Header from '@/components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Info, Target, Scale, Shield, FileText } from 'lucide-react';
import Footer from '@/components/Footer';

const About = () => {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <main className="container px-4 py-8 md:py-12 flex-grow">
        <Link to="/" className="mb-6 inline-block">
          <Button variant="outline" size="sm">
            <ArrowLeft size={16} className="mr-2" />
            Back to Promise Tracker
          </Button>
        </Link>

        <Card className="shadow-lg border-t-4 border-nepal-blue">
          <CardHeader>
            <CardTitle className="text-3xl font-bold text-center text-nepal-red">
              About भेडा कसम (Bheda Kasam)
            </CardTitle>
          </CardHeader>
          <CardContent className="prose prose-lg max-w-none mx-auto text-gray-700 space-y-6">
            <p className="text-center text-xl font-medium text-gray-600 italic bg-gray-50 py-4 rounded-lg border border-gray-100">
              Tracking Political Promises, Fostering Accountability in Nepal.
            </p>

            <Separator className="my-8" />

            <section>
              <div className="flex items-center gap-2 mb-4">
                <Info className="h-6 w-6 text-nepal-blue" />
                <h2 className="text-2xl font-semibold text-nepal-blue">What is Bheda Kasam?</h2>
              </div>
              <p className="pl-8">
                Bheda Kasam ("Sheep's Oath" in Nepali, invoking a sense of serious commitment) is an independent, non-partisan platform dedicated to tracking the promises made by political leaders and parties in Nepal. We believe that a healthy democracy thrives on informed citizens and accountable leadership. Our goal is to provide a clear, accessible record of political commitments, allowing the public to follow their progress – or lack thereof.
              </p>
            </section>

            <section>
              <div className="flex items-center gap-2 mb-4">
                <Target className="h-6 w-6 text-nepal-blue" />
                <h2 className="text-2xl font-semibold text-nepal-blue">Our Mission</h2>
              </div>
              <div className="pl-8">
                <p>
                  Our mission is simple: to enhance transparency and accountability in Nepali politics. By meticulously documenting and tracking political promises, we aim to:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <ul className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                    <li className="mb-2">✦ Empower citizens with factual information about their leaders' commitments.</li>
                    <li className="mb-2">✦ Encourage politicians and parties to be more mindful and realistic in their promises.</li>
                  </ul>
                  <ul className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                    <li className="mb-2">✦ Foster constructive public discourse based on performance and follow-through.</li>
                    <li>✦ Provide a historical record of political pledges for future reference and analysis.</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <div className="flex items-center gap-2 mb-4">
                <Scale className="h-6 w-6 text-nepal-blue" />
                <h2 className="text-2xl font-semibold text-nepal-blue">Our Approach</h2>
              </div>
              <div className="pl-8">
                <p className="mb-4">
                  We monitor public statements, official documents, manifestos, and credible news sources to gather political promises. Each promise is documented with details such as the politician/party involved, the date made, the sector it relates to, and any stated deadlines.
                </p>
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 border-b">Promise Status</th>
                        <th className="px-4 py-2 border-b">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="px-4 py-2 border-b font-medium text-green-600">Fulfilled</td>
                        <td className="px-4 py-2 border-b">Promise has been completely implemented with verifiable evidence</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 border-b font-medium text-yellow-600">Ongoing</td>
                        <td className="px-4 py-2 border-b">Work is in progress with demonstrable steps taken</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 border-b font-medium text-red-600">Broken</td>
                        <td className="px-4 py-2 border-b">Promise has been explicitly abandoned or deadline passed without fulfillment</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 border-b font-medium text-gray-600">Unknown</td>
                        <td className="px-4 py-2 border-b">Insufficient information to determine status</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
            
            <section>
              <div className="flex items-center gap-2 mb-4">
                <Shield className="h-6 w-6 text-nepal-blue" />
                <h2 className="text-2xl font-semibold text-nepal-blue">We Are Independent</h2>
              </div>
              <p className="pl-8">
                Bheda Kasam operates with complete independence. We are not affiliated with, funded by, or influenced by any political party, politician, government entity, or special interest group. Our commitment is solely to providing objective information to the Nepali public.
              </p>
            </section>

            <Separator className="my-8" />

            <section>
              <div className="flex items-center gap-2 mb-4">
                <FileText className="h-6 w-6 text-nepal-blue" />
                <h2 className="text-2xl font-semibold text-nepal-blue">Legal Framework & Disclaimer</h2>
              </div>
              <div className="pl-8 space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                  <h3 className="font-medium mb-2">Freedom of Information</h3>
                  <p>
                    This platform operates in the spirit of promoting transparency and access to information, fundamental aspects of democratic societies. We exercise the right to gather and disseminate public information concerning political commitments as part of fostering an informed citizenry, in accordance with the principles of freedom of expression enshrined in <a href="https://ag.gov.np/files/Constitution-of-Nepal_2072_Eng_www.moljpa.gov_.npDate-72_11_16.pdf#page=17" target="_blank" rel="noopener noreferrer" className="text-nepal-blue hover:underline">Article 17</a> and <a href="https://ag.gov.np/files/Constitution-of-Nepal_2072_Eng_www.moljpa.gov_.npDate-72_11_16.pdf#page=19" target="_blank" rel="noopener noreferrer" className="text-nepal-blue hover:underline">Article 19</a> of the Constitution of Nepal.
                  </p>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                  <h3 className="font-medium mb-2">Disclaimer</h3>
                  <p>
                    The information presented on Bheda Kasam is compiled from publicly available sources. While we strive for accuracy and timeliness, we rely on the availability and reliability of these external sources. The status assigned to each promise represents our assessment based on the evidence gathered, but may be subject to interpretation or change as new information becomes available.
                  </p>
                  <p className="mt-2">
                    This platform is intended for informational and civic engagement purposes. The inclusion of a promise or information about a politician/party does not imply endorsement or criticism. We encourage users to conduct their own research and form their own conclusions. Images used on the site are often sourced from publicly available platforms for illustrative purposes only.
                  </p>
                </div>
              </div>
            </section>

          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
};

export default About; 